import { z } from 'zod';

import { PATHS } from '@/shared/constants/routes/paths';

export const GUEST_PAID_TRIAL_CLAIM_STORAGE_KEY = 'rask.guestPaidTrial.claims.v2';
const LEGACY_CLAIM_STORAGE_KEY = 'rask.guestPaidTrial.claim.v1';
const MAX_STORED_CLAIMS = 5;

export const EMAIL_CLAIM_SESSION_PARAM = 'session_id';
export const EMAIL_CLAIM_SECRET_PARAM = 'claim';

const sessionIdSchema = z.string().max(255).regex(/^cs_/);
const claimSchema = z.object({
  claim_secret: z.string().min(32).max(128),
  session_id: sessionIdSchema,
});
const storedClaimSchema = z.object({
  claim_secret: claimSchema.shape.claim_secret,
  paid: z.literal(true).optional(),
  signup_rejected: z.literal(true).optional(),
  created_at: z.number().finite(),
});
const legacyClaimSchema = storedClaimSchema.extend({ session_id: sessionIdSchema });

export type GuestPaidTrialClaim = {
  claim_secret: string;
  session_id: string;
};
type StoredClaim = z.infer<typeof storedClaimSchema>;
type StoredClaimEntry = [string, StoredClaim];

const readJson = (key: string): unknown => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null');
  } catch {
    return null;
  }
};

const readStoredClaims = (): Record<string, StoredClaim> => {
  const claims: Record<string, StoredClaim> = {};
  const stored = readJson(GUEST_PAID_TRIAL_CLAIM_STORAGE_KEY);
  for (const [sessionId, value] of Object.entries(stored && typeof stored === 'object' ? stored : {})) {
    const parsed = storedClaimSchema.safeParse(value);
    if (parsed.success && sessionIdSchema.safeParse(sessionId).success) claims[sessionId] = parsed.data;
  }
  const legacy = legacyClaimSchema.safeParse(readJson(LEGACY_CLAIM_STORAGE_KEY));
  if (legacy.success && !claims[legacy.data.session_id]) {
    const { session_id, ...claim } = legacy.data;
    claims[session_id] = claim;
  }
  return claims;
};

const byPreference = ([, a]: StoredClaimEntry, [, b]: StoredClaimEntry) =>
  Number(Boolean(b.paid)) - Number(Boolean(a.paid)) || b.created_at - a.created_at;

const writeStoredClaims = (claims: Record<string, StoredClaim>) => {
  const kept = Object.entries(claims).sort(byPreference).slice(0, MAX_STORED_CLAIMS);
  try {
    localStorage.removeItem(LEGACY_CLAIM_STORAGE_KEY);
    if (kept.length) localStorage.setItem(GUEST_PAID_TRIAL_CLAIM_STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)));
    else localStorage.removeItem(GUEST_PAID_TRIAL_CLAIM_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
};

export const saveGuestPaidTrialClaim = (claim: GuestPaidTrialClaim, { paid = false }: { paid?: boolean } = {}) => {
  const parsed = claimSchema.safeParse(claim);
  if (!parsed.success) return false;
  const { claim_secret, session_id } = parsed.data;
  const claims = readStoredClaims();
  const existing = claims[session_id]?.claim_secret === claim_secret ? claims[session_id] : undefined;
  claims[session_id] = { ...existing, claim_secret, created_at: Date.now(), ...(paid && { paid: true }) };
  return writeStoredClaims(claims);
};

const updateStoredClaim = (sessionId: string, patch: Partial<StoredClaim>) => {
  const claims = readStoredClaims();
  if (!claims[sessionId]) return;
  claims[sessionId] = { ...claims[sessionId], ...patch };
  writeStoredClaims(claims);
};

export const markGuestPaidTrialClaimPaid = (sessionId: string) =>
  updateStoredClaim(sessionId, { paid: true, created_at: Date.now() });

export const rejectGuestPaidTrialSignupClaim = (sessionId: string) =>
  updateStoredClaim(sessionId, { signup_rejected: true });

const pickClaim = (keep: (entry: StoredClaimEntry) => boolean): GuestPaidTrialClaim | null => {
  const [entry] = Object.entries(readStoredClaims()).filter(keep).sort(byPreference);
  return entry ? { claim_secret: entry[1].claim_secret, session_id: entry[0] } : null;
};

export const readGuestPaidTrialClaim = () => pickClaim(() => true);

export const readGuestPaidTrialClaimForSession = (sessionId: string) => pickClaim(([id]) => id === sessionId);

export const readGuestPaidTrialSignupClaim = () => pickClaim(([, claim]) => !claim.signup_rejected);

export const keepGuestPaidTrialClaims = () => {
  const claims = readStoredClaims();
  return () => void writeStoredClaims(claims);
};

export const clearGuestPaidTrialClaim = (sessionId?: string) => {
  if (!sessionId) {
    writeStoredClaims({});
    return;
  }
  const claims = readStoredClaims();
  delete claims[sessionId];
  writeStoredClaims(claims);
};

// A guest-claim 409 carries only its detail; keep these byte-identical to localization/exceptions/payment.py.
const SIGNUP_NOTICE_BY_CONFLICT_DETAIL = {
  'Sign up with the email you paid with to link this payment.': 'paidTrial.claimEmailMismatch',
  "This payment can't be linked to a new account automatically. Please contact support.": 'paidTrial.claimNotClaimable',
  'This payment is already linked to another account.': 'paidTrial.claimAlreadyClaimed',
} as const;
type GuestPaidTrialSignupNotice =
  (typeof SIGNUP_NOTICE_BY_CONFLICT_DETAIL)[keyof typeof SIGNUP_NOTICE_BY_CONFLICT_DETAIL];
const SIGNUP_NOTICES = new Set<string>(Object.values(SIGNUP_NOTICE_BY_CONFLICT_DETAIL));

export const GUEST_PAID_TRIAL_SIGNUP_NOTICE_STORAGE_KEY = 'rask.guestPaidTrial.signupNotice';

export const rememberGuestPaidTrialSignupNotice = (error: unknown): GuestPaidTrialSignupNotice | null => {
  const detail = (error as { data?: { detail?: unknown } } | null)?.data?.detail;
  if (typeof detail !== 'string' || !Object.hasOwn(SIGNUP_NOTICE_BY_CONFLICT_DETAIL, detail)) return null;
  const notice = SIGNUP_NOTICE_BY_CONFLICT_DETAIL[detail as keyof typeof SIGNUP_NOTICE_BY_CONFLICT_DETAIL];
  try {
    sessionStorage.setItem(GUEST_PAID_TRIAL_SIGNUP_NOTICE_STORAGE_KEY, notice);
    return notice;
  } catch {
    return null;
  }
};

export const takeGuestPaidTrialSignupNotice = (): GuestPaidTrialSignupNotice | null => {
  try {
    const notice = sessionStorage.getItem(GUEST_PAID_TRIAL_SIGNUP_NOTICE_STORAGE_KEY);
    sessionStorage.removeItem(GUEST_PAID_TRIAL_SIGNUP_NOTICE_STORAGE_KEY);
    return notice && SIGNUP_NOTICES.has(notice) ? (notice as GuestPaidTrialSignupNotice) : null;
  } catch {
    return null;
  }
};

export const consumeEmailedGuestPaidTrialClaim = () => {
  const url = new URL(window.location.href);
  const claim_secret = url.searchParams.get(EMAIL_CLAIM_SECRET_PARAM);
  if (url.pathname.replace(/\/+$/, '') !== `/${PATHS.guestTrial}` || !claim_secret) return;
  const session_id = url.searchParams.get(EMAIL_CLAIM_SESSION_PARAM);
  if (session_id) saveGuestPaidTrialClaim({ claim_secret, session_id }, { paid: true });
  url.searchParams.delete(EMAIL_CLAIM_SESSION_PARAM);
  url.searchParams.delete(EMAIL_CLAIM_SECRET_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};
